import React, {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useDropzone} from "react-dropzone";
import {Box, Typography, List, ListItem, IconButton, Stack, Avatar, Link} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import {FileIcon} from "../icons/FileIcon";
import {CloudUploadIcon} from "../icons/CloudUploadIcon";

interface FileDropZoneProps {
    files: File[];
    setFiles: (value: ((prevState: File[]) => File[]) | File[]) => void;
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({files, setFiles}) => {
    const {t} = useTranslation();

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
        },
        [setFiles],
    );

    const onUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement & {files: FileList};
        const files = target.files;
        setFiles((prevFiles) => [...prevFiles, ...files]);
    };

    const onRemove = (fileName: string) => {
        setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
    };

    // noClick prevents opening the upload window twice, in case parent is label
    // https://github.com/react-dropzone/react-dropzone/issues/1107
    const {getRootProps, getInputProps, open} = useDropzone({onDrop, noClick: true});

    return (
        <>
            <Stack direction="column" gap="1rem">
                <Stack direction="row" gap="1rem">
                    <Typography fontSize={16} fontWeight={500} textTransform="uppercase">
                        {t("file_dropzone.title_attachments")}
                    </Typography>
                    <Avatar
                        variant="rounded"
                        sx={{
                            bgcolor: "#CBD3F0",
                            color: "#303A8C",
                            width: 24,
                            height: 24,
                            fontSize: 14,
                            fontWeight: 500,
                        }}
                    >
                        {files.length}
                    </Avatar>
                </Stack>
                {files.length > 0 && (
                    <Box sx={{maxHeight: "200px", overflow: "auto", paddingLeft: "0.5rem"}}>
                        <List>
                            {files.map((file) => (
                                <ListItem key={file.name} disablePadding dense>
                                    <Stack width="100%" direction="row" justifyContent="space-between">
                                        <Stack direction="row" alignItems="center" gap="0.6rem">
                                            <FileIcon
                                                sx={{
                                                    stroke: "#8B9ADA",
                                                    width: "1.125rem",
                                                    height: "1.125rem",
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {file.name}
                                            </Typography>
                                        </Stack>
                                        <IconButton
                                            aria-label="delete"
                                            color="primary"
                                            sx={{padding: "0.2rem"}}
                                            onClick={() => onRemove(file.name)}
                                        >
                                            <ClearIcon color="error" />
                                        </IconButton>
                                    </Stack>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
                <Box
                    width={"100%"}
                    {...getRootProps()}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 200,
                        border: `2px dashed #DBDEE6`,
                        backgroundColor: "#FAFCFF",
                        borderRadius: "4px",
                        backdropFilter: "blur(5px) sepia(5%)",
                    }}
                >
                    <Stack width="100%" direction="column" justifyContent="center" alignItems="center" gap="0.75rem">
                        <input
                            multiple
                            type="file"
                            style={{display: "none"}}
                            id="file-upload"
                            onChange={onUpload}
                            {...getInputProps()}
                        />
                        <Avatar
                            variant="circular"
                            sx={{
                                bgcolor: "#F2F4F7",
                                width: "2.5rem",
                                height: "2.5rem",
                            }}
                        >
                            <CloudUploadIcon
                                sx={{
                                    stroke: "#98A2B3",
                                    width: "1.125rem",
                                    height: "1.125rem",
                                }}
                            />
                        </Avatar>
                        <Typography>
                            <label htmlFor="file-upload">
                                <Link href="#" underline="none" onClick={open}>
                                    {t("file_dropzone.button")}
                                </Link>{" "}
                                {t("file_dropzone.drop")}
                            </label>
                        </Typography>
                    </Stack>
                </Box>
            </Stack>
        </>
    );
};
